import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import OrgQRCode from './OrgQRCode';

function OrgSetup({ onSelectOrg }) {
  const [organizations, setOrganizations] = useState([]);
  const [orgName, setOrgName] = useState('');
  const [orgType, setOrgType] = useState('school');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [qrOrg, setQrOrg] = useState(null);

  useEffect(() => {
    checkIfSuperAdmin();
    fetchOrganizations();
    handleOrgFromUrl();
  }, []);

  const handleOrgFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    const orgId = params.get('org');
    if (orgId) {
      fetchOrgAndSelect(orgId);
    }
  };

  const fetchOrgAndSelect = async (orgId) => {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', orgId)
      .single();

    if (!error && data) {
      onSelectOrg(data, true);
    }
  };

  const checkIfSuperAdmin = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return;

    const { data, error } = await supabase
      .from('profiles')
      .select('role')
      .eq('id', session.user.id)
      .single();

    if (!error && data.role === 'superadmin') {
      setIsSuperAdmin(true);
    }
  };

  const fetchOrganizations = async () => {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      setOrganizations([]);
      setLoading(false);
      return;
    }

    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('role, organization_id')
      .eq('id', session.user.id)
      .single();

    if (profileError) {
      setLoading(false);
      return;
    }

    if (profile.role === 'superadmin') {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .order('created_at', { ascending: false });
      if (!error) setOrganizations(data);
    } else if (profile.organization_id) {
      const { data, error } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', profile.organization_id);
      if (!error) setOrganizations(data);
    } else {
      setOrganizations([]);
    }

    setLoading(false);
  };

  const handleCreateOrg = async () => {
    if (!orgName) {
      setMessage('Please enter an organization name.');
      return;
    }

    const { error } = await supabase
      .from('organizations')
      .insert([{ name: orgName, type: orgType }]);

    if (error) {
      setMessage('Error: ' + error.message);
      return;
    }

    setOrgName('');
    setShowForm(false);
    setMessage('');
    fetchOrganizations();
  };

  const handleDeleteOrg = async (orgId) => {
    const { error } = await supabase
      .from('organizations')
      .delete()
      .eq('id', orgId);

    if (error) {
      setMessage('Error deleting: ' + error.message);
      return;
    }

    setConfirmDelete(null);
    fetchOrganizations();
  };

  const getOrgIcon = (type) => {
    if (type === 'school') return '🏫';
    if (type === 'daycare') return '🧸';
    if (type === 'church') return '⛪';
    if (type === 'event') return '🎉';
    return '🏢';
  };

  return (
    <div>
      {qrOrg && <OrgQRCode org={qrOrg} onClose={() => setQrOrg(null)} />}

      <div className="header">
        <h1>🏫 SafeRoster</h1>
        <p>Select or create your organization</p>
      </div>

      {isSuperAdmin && !showForm && (
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>
          + Create New Organization
        </button>
      )}

      {isSuperAdmin && showForm && (
        <div style={{ marginBottom: '24px', background: '#f8f9ff', borderRadius: '16px', padding: '20px' }}>
          <p className="section-title">New Organization</p>
          <input className="input" placeholder="Organization Name" value={orgName} onChange={(e) => setOrgName(e.target.value)} />
          <select className="input" value={orgType} onChange={(e) => setOrgType(e.target.value)}>
            <option value="school">School</option>
            <option value="daycare">Daycare</option>
            <option value="church">Church / Sunday School</option>
            <option value="event">Event</option>
          </select>
          <button className="btn btn-primary" onClick={handleCreateOrg}>Create Organization</button>
          <button className="btn btn-gray" style={{ width: '100%', marginTop: '10px' }} onClick={() => setShowForm(false)}>Cancel</button>
          {message && <div className="error-message">{message}</div>}
        </div>
      )}

      <hr className="divider" />

      <p className="section-title">Your Organizations</p>

      {loading && <p style={{ textAlign: 'center', color: '#888' }}>Loading...</p>}

      {!loading && organizations.length === 0 && (
        <p style={{ textAlign: 'center', color: '#999' }}>No organizations yet. Create one above!</p>
      )}

      {organizations.map((org) => (
        <div key={org.id}>
          {confirmDelete === org.id && (
            <div style={{ background: '#fdecea', borderRadius: '12px', padding: '16px', marginBottom: '8px' }}>
              <p style={{ color: '#c0392b', fontWeight: '600', marginBottom: '12px' }}>
                Are you sure you want to delete <strong>{org.name}</strong>? This cannot be undone.
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button className="btn btn-red btn-small" style={{ flex: 1 }} onClick={() => handleDeleteOrg(org.id)}>
                  Yes, Delete
                </button>
                <button className="btn btn-gray btn-small" style={{ flex: 1 }} onClick={() => setConfirmDelete(null)}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
            <div className="org-card" style={{ flex: 1 }} onClick={() => onSelectOrg(org)}>
              <div className="org-icon">{getOrgIcon(org.type)}</div>
              <div>
                <h3>{org.name}</h3>
                <p>{org.type}</p>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <button
                className="btn btn-blue btn-small"
                onClick={() => setQrOrg(org)}
              >
                📱 QR
              </button>
              {isSuperAdmin && (
                <button
                  className="btn btn-red btn-small"
                  onClick={() => setConfirmDelete(org.id)}
                >
                  🗑️
                </button>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default OrgSetup;